import { useLocation, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, MessageCircle, Home, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { useConfig, whatsappLink } from "@/context/ConfigContext";
import { formatNaira, formatDate } from "@/lib/format";

export default function Confirmation() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const config = useConfig();
  const order = state?.order;
  const failed = state?.failed;
  const pending = state?.pendingPayment;

  if (!order && !failed) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-2xl px-5 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground">No order to display</h1>
          <p className="mt-2 text-muted-foreground">Start a new reservation from the homepage.</p>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-sm bg-primary text-primary-foreground hover:bg-[#003DB3]">
            <Home className="mr-2 h-4 w-4" /> Back to home
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (failed) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-2xl px-5 py-24 text-center">
          <XCircle className="mx-auto h-14 w-14 text-destructive" />
          <h1 className="mt-6 font-heading text-3xl font-bold text-foreground">Payment not completed</h1>
          <p className="mt-3 text-muted-foreground">{state?.message || "Your payment was not successful. No reservation has been created. You can try again or reach us on WhatsApp."}</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button onClick={() => navigate("/order")} data-testid="retry-order" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#003DB3]">Try again</Button>
            <a href={whatsappLink(config)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-sm border border-border px-6 py-2.5 font-semibold hover:border-primary hover:text-primary">
              <MessageCircle className="h-4 w-4" /> WhatsApp us
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const rows = [
    ["Order number", order.order_number],
    ["Service", order.service],
    ["Processing time", order.processing_time],
    ["Destination", order.destination],
    ["Departure date", formatDate(order.departure_date)],
    ["Return date", order.return_date ? formatDate(order.return_date) : "—"],
    ["Amount", formatNaira(order.amount)],
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-2xl px-5 py-16 lg:py-20">
        <div className="text-center">
          {pending ? (
            <>
              <Clock className="mx-auto h-14 w-14 text-primary" />
              <h1 className="mt-6 font-heading text-3xl font-bold tracking-tight text-foreground" data-testid="confirmation-title">Order received — payment pending</h1>
              <p className="mt-3 text-muted-foreground">We've saved your order details. Please contact us on WhatsApp to complete your payment and start processing.</p>
            </>
          ) : (
            <>
              <CheckCircle2 className="mx-auto h-14 w-14 text-[#0F4C3A]" />
              <h1 className="mt-6 font-heading text-3xl font-bold tracking-tight text-foreground" data-testid="confirmation-title">Payment confirmed</h1>
              <p className="mt-3 text-muted-foreground">Thank you. Your reservation is being prepared and will be delivered within your selected processing time.</p>
            </>
          )}
        </div>

        <div className="mt-10 rounded-sm border border-border bg-card p-6 lg:p-8">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Your order number</p>
          <p className="font-heading text-3xl font-extrabold text-primary" data-testid="confirmation-order-number">{order.order_number}</p>
          <dl className="mt-6 divide-y divide-border">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 py-3 text-sm">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-right font-medium text-foreground">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mt-6 rounded-sm border border-border bg-secondary p-5 text-sm leading-relaxed text-foreground">
          <strong>Reminder:</strong> This is a confirmed airline reservation for visa documentation — not a purchased airline ticket.
          VisaFlight does not guarantee visa approval. Processing begins after payment and receipt of all required information.
          One itinerary change is included.
        </div>

        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <a
            href={whatsappLink(config, `Hello VisaFlight, this is regarding my order ${order.order_number}.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="confirmation-whatsapp"
            className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#25D366] px-6 py-3 font-semibold text-white transition-transform hover:scale-[1.02]"
          >
            <MessageCircle className="h-5 w-5" /> Contact us on WhatsApp
          </a>
          <Link to="/" className="inline-flex items-center justify-center gap-2 rounded-sm border border-border px-6 py-3 font-semibold text-foreground hover:border-primary hover:text-primary">
            <Home className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
